import { useEffect, useMemo, useState } from 'react';

function canonicalVehicleId(vehicle) {
  if (vehicle?.maccezVehicleId) return Number(vehicle.maccezVehicleId);
  // /api/devices uses the MACCEZ id; Traccar device.id is a different key.
  if (vehicle?.id && (!vehicle.source || vehicle.source === 'maccez-database')) return Number(vehicle.id);
  return null;
}

export default function DriverRecognitionCard({ vehicle, busyControl, onProfile }) {
  const id = canonicalVehicleId(vehicle);
  const imei = String(vehicle?.imei || vehicle?.uniqueId || '');
  const owner = String(vehicle?.owner_user_id || '');
  const key = useMemo(() => `${id || ''}:${imei}:${owner}`, [id, imei, owner]);
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [reload, setReload] = useState(0);
  const sensor = loaded?.key === key ? loaded.data : null;

  useEffect(() => {
    if (!id || !imei || vehicle?.licenseValid === false) return;
    const controller = new AbortController();
    setError('');
    setErrorCode('');
    async function load() {
      try {
        const response = await fetch(`/api/devices/${id}/driver-recognition`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('No se pudo cargar el estado del sensor');
        const data = await response.json();
        if (Number(data.vehicleId) !== id || String(data.imei) !== imei) {
          throw new Error('La respuesta no corresponde al activo seleccionado');
        }
        if (!controller.signal.aborted) {
          setLoaded({ key, data });
          onProfile?.(data);
          setError(data.verificationError || '');
          setErrorCode(data.verificationError ? 'VERIFY_FAILED' : '');
        }
      } catch (cause) {
        if (cause.name !== 'AbortError' && !controller.signal.aborted) {
          setError(cause.message);
          setErrorCode('LOAD_FAILED');
        }
      }
    }
    load();
    const timer = setInterval(load, 30000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [id, imei, key, vehicle?.licenseValid, onProfile, reload]);

  async function changeMode() {
    if (!sensor?.configured || !sensor.connected || !sensor.benchVerified ||
        !sensor.remoteControlAvailable || loading || busyControl || sensor.mode === 'unknown') return;
    const target = sensor.mode === 'bypassed' ? 'protected' : 'bypassed';
    if (!window.confirm(target === 'bypassed'
      ? 'Apaga el vehículo y espera 30 segundos, Despues pulsa OK para deshabilitar el reconocimiento de conductor'
      : 'Para habilitar el sensor, apaga el vehículo y ten el sensor dentro del vehículo. ¿Continuar?')) return;
    setLoading(true);
    setError('');
    setErrorCode('');
    try {
      const response = await fetch(`/api/devices/${id}/driver-recognition/mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ mode: target })
      });
      const data = await response.json();
      if (!response.ok) {
        const failure = new Error(data.message || 'No se pudo confirmar el cambio.');
        failure.code = data.code;
        throw failure;
      }
      setLoaded({ key, data: { ...sensor, ...data } });
      onProfile?.({ ...sensor, ...data });
    } catch (cause) {
      setError(cause.message);
      setErrorCode(cause.code || 'VERIFY_FAILED');
      // Una respuesta perdida puede llegar después del cambio físico.
      // Se consulta de nuevo antes de ofrecer cualquier otro comando.
      setReload(value => value + 1);
    } finally {
      setLoading(false);
    }
  }

  const configured = sensor?.configured === true;
  const enabled = configured && sensor.mode === 'protected';
  const disabled = configured && sensor.mode === 'bypassed';
  const canControl = configured && sensor.benchVerified && sensor.remoteControlAvailable;
  const devices = configured ? (sensor.sensors || []) : [];

  return <section className="control-sensor" aria-label="Reconocimiento de conductor">
    <div className="control-sensor-top">
      <span className="control-sensor-icon" aria-hidden="true">◈</span>
      <div>
        <h2>Reconocimiento de conductor</h2>
        <p>{enabled ? 'Sensor habilitado' : disabled ? 'Sensor deshabilitado' :
          configured ? 'Sensor asignado · estado por confirmar' :
          error ? 'No se pudo cargar el sensor' :
          sensor ? 'Consulta por tu sensor' : 'Cargando estado guardado…'}</p>
      </div>
    </div>
    {sensor && !configured && <p className="control-sensor-hint">Agrega reconocimiento de conductor a este vehículo.</p>}
    {configured && <>
      <div className="control-sensor-list">
        <strong>{devices.length} {devices.length === 1 ? 'sensor asignado' : 'sensores asignados'}</strong>
        <div>{devices.map((device, index) =>
          <span key={index} className="control-sensor-chip">{device.name}</span>)}</div>
      </div>
     
      {canControl && <button type="button" className="control-sensor-action"
        disabled={loading || busyControl || !sensor.connected || vehicle?.licenseValid === false}
        onClick={sensor.mode === 'unknown' ? () => setReload(value => value + 1) : changeMode}>
        {loading ? 'Verificando estado…' : sensor.mode === 'unknown' ? 'Verificar estado' :
          disabled ? 'Habilitar sensor' : 'Deshabilitar sensor'}
      </button>}
      {!sensor.connected && <small>Enciende el vehículo para actualizar el estado.</small>}
      {errorCode === 'NEED_IGNITION_OFF' && <small>Si sigue verificando, enciende y vuelve a apagar el vehículo.</small>}
      {!canControl && <small>No se pudo confirmar la configuración del sensor.</small>}
    </>}
    {error && <p role="alert" className="control-error">{error}</p>}
  </section>;
}
